import { useMemo } from 'react'
import { Clock } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useActivityHeatmap } from '@/services/admin/metrics.service'
import { cn } from '@/lib/utils'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
  className?: string
}

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
// MariaDB DAYOFWEEK: 1=domingo..7=sábado → mapeia direto

/**
 * Heatmap 7 dias × 24 horas. Cores em escala (do mais frio ao quente)
 * baseadas na densidade de eventos.
 *
 * Cada célula é um div com background dinâmico calculado client-side
 * (em vez de CSS class) — Tailwind não tem opacity arbitrário fácil.
 */
export function ActivityHeatmap({ filters, className }: Props) {
  const { data, isLoading } = useActivityHeatmap({
    companyId: filters.companyIds,
    dateFrom: filters.dateFrom?.toISOString(),
  })

  // Constroi um Map<"weekday-hour", count> pra lookup O(1)
  const { cellMap, maxCount, totalCount } = useMemo(() => {
    const map = new Map<string, number>()
    let max = 0
    let total = 0
    for (const c of data?.cells ?? []) {
      const k = `${c.weekday}-${c.hour}`
      map.set(k, c.count)
      if (c.count > max) max = c.count
      total += c.count
    }
    return { cellMap: map, maxCount: max, totalCount: total }
  }, [data])

  function colorFor(count: number): string {
    if (count === 0) return 'rgba(120,120,120,0.05)'
    const ratio = maxCount > 0 ? count / maxCount : 0
    // Escala laranja FCamara — alpha de 0.15 a 0.95
    const alpha = 0.15 + ratio * 0.8
    return `rgba(242, 101, 34, ${alpha.toFixed(2)})`
  }

  if (isLoading) {
    return (
      <Card className={cn('glass-card border-border/50', className)}>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn('glass-card border-border/50', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="w-4 h-4 text-primary" />
          Mapa de atividade
        </CardTitle>
        <CardDescription>
          Quando os alunos estudam — {totalCount} episódios completos · últimos 90d
        </CardDescription>
      </CardHeader>
      <CardContent>
        {totalCount === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Sem atividade registrada.
          </p>
        ) : (
          <div className="space-y-2">
            {/* Grid 7×24 (uma linha por dia). `grid w-full` faz as colunas
                expandirem pra largura disponível do card — antes era
                `inline-grid` e o grid colapsava no mínimo de 14px, deixando
                muito espaço vazio à direita. */}
            <div className="overflow-x-auto">
              <div
                className="grid w-full gap-0.5"
                style={{
                  gridTemplateColumns: 'auto repeat(24, minmax(14px, 1fr))',
                }}
              >
                {/* Header com horas */}
                <div />
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={`h-${h}`}
                    className="text-[9px] text-muted-foreground text-center tabular-nums"
                  >
                    {h % 3 === 0 ? h : ''}
                  </div>
                ))}
                {/* Linhas: 1=domingo..7=sábado mapeado */}
                {WEEKDAY_LABELS.map((label, idx) => {
                  const weekday = idx + 1
                  return (
                    <>
                      <div
                        key={`d-${weekday}`}
                        className="text-[10px] text-muted-foreground pr-2 self-center font-medium"
                      >
                        {label}
                      </div>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const count = cellMap.get(`${weekday}-${hour}`) ?? 0
                        return (
                          <div
                            key={`${weekday}-${hour}`}
                            // sem border: a moldura fina deixava o
                            // preenchimento parecendo um "ponto dentro de um
                            // quadrado". Agora a cor encosta nas bordas.
                            className="aspect-square rounded-sm"
                            style={{ backgroundColor: colorFor(count) }}
                            title={`${label} ${hour}h — ${count} eventos`}
                          />
                        )
                      })}
                    </>
                  )
                })}
              </div>
            </div>

            {/* Legenda */}
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground justify-end pt-2">
              <span>Menos</span>
              {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-sm border border-border/20"
                  style={{ backgroundColor: colorFor(Math.round(r * maxCount)) }}
                />
              ))}
              <span>Mais</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
